import React from 'react'
import { Card, Pill } from '../../ui'
import { InlineManagerNote } from '../annotations/InlineManagerNote.jsx'

const STATUS_TONE = {
  done: 'success',
  on_track: 'info',
  at_risk: 'neutral',
  blocked: 'neutral',
  not_started: 'neutral',
}

/**
 * Card 1 for Successes and Challenges tracks.
 * Shows a chip-based picker of eligible sub-goals plus an optional note textarea.
 *
 * Props:
 * - subgoals: all available sub-goals to pick from (filtered by status externally)
 * - selectedIds: array of selected subgoalIds
 * - onToggle(subgoalId): toggle selection
 * - note: free-text note value
 * - onNoteChange(value): note change handler
 * - snapshots: frozen sub-goal snapshots (when closed)
 * - tag/title/placeholder/notePlaceholder: display strings
 */
export function SubgoalPickerCard({
  tag,
  title,
  subgoals = [],
  selectedIds = [],
  onToggle,
  note = '',
  onNoteChange,
  notePlaceholder = '',
  placeholder = '',
  snapshots = null,
  readOnly = false,
  isClosed = false,
  managerNote = '',
  onManagerNoteChange,
  onSuggestManagerNote,
  isManager = false,
  isReady = false,
  isShared = false,
}) {
  const selectedSet = new Set(selectedIds)

  // After close, render from snapshots
  if (isClosed && snapshots) {
    return (
      <Card style={{ background: 'var(--locked-bg)' }}>
        <div style={styles.container}>
          <span style={styles.tag}>{tag}</span>
          <h4 style={styles.title}>{title}</h4>
          <div style={styles.chips}>
            {snapshots.map((snap) => (
              <Pill key={snap.subgoalId} tone={STATUS_TONE[snap.status] || 'neutral'}>
                {snap.title}
              </Pill>
            ))}
            {snapshots.length === 0 && (
              <span style={styles.muted}>None selected</span>
            )}
          </div>
          {note && <p style={styles.noteText}>{note}</p>}
          <InlineManagerNote
            value={managerNote}
            onChange={onManagerNoteChange}
            onSuggest={onSuggestManagerNote}
            isManager={isManager}
            isReady={isReady}
            isShared={isShared}
            isClosed={isClosed}
          />
        </div>
      </Card>
    )
  }

  return (
    <Card style={isClosed ? { background: 'var(--locked-bg)' } : undefined}>
      <div style={styles.container}>
        <span style={styles.tag}>{tag}</span>
          <h4 style={styles.title}>{title}</h4>

          {!readOnly && subgoals.length === 0 && (
            <span style={styles.muted}>{placeholder || 'No eligible sub-goals'}</span>
          )}

          <div style={styles.chips}>
            {subgoals.map((sg) => {
              const isSelected = selectedSet.has(sg.subgoalId)
              const statusChanged = isSelected && sg._statusChanged
              return (
                <button
                  key={sg.subgoalId}
                  type="button"
                  style={{
                    ...styles.chip,
                    background: isSelected ? 'var(--accent-brand)' : 'transparent',
                    color: isSelected ? '#fff' : 'var(--text-primary)',
                    borderColor: isSelected ? 'var(--accent-brand)' : 'var(--border)',
                    cursor: readOnly ? 'default' : 'pointer',
                    opacity: readOnly && !isSelected ? 0.5 : 1,
                  }}
                  onClick={() => !readOnly && onToggle?.(sg.subgoalId)}
                  disabled={readOnly}
                  aria-pressed={isSelected}
                  aria-label={sg.title}
                >
                  {sg.title}
                  {statusChanged && <span style={styles.badge}>changed</span>}
                </button>
              )
            })}
          </div>

          <textarea
            style={{
              ...styles.textarea,
              background: 'transparent',
            }}
            value={note}
            onChange={(e) => onNoteChange?.(e.target.value)}
            placeholder={readOnly ? '' : notePlaceholder}
            disabled={readOnly}
            rows={2}
            aria-label={`${title} note`}
          />
        <InlineManagerNote
          value={managerNote}
          onChange={onManagerNoteChange}
          isManager={isManager}
          isReady={isReady}
          isShared={isShared}
          isClosed={isClosed}
        />
      </div>
    </Card>
  )
}

const styles = {
  container: { display: 'flex', flexDirection: 'column', gap: '8px' },
  tag: {
    fontSize: '10px',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    color: 'var(--text-secondary)',
  },
  title: {
    fontSize: '13px',
    fontWeight: 600,
    color: 'var(--text-primary)',
    margin: 0,
  },
  chips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px',
  },
  chip: {
    padding: '4px 10px',
    borderRadius: '20px',
    border: '1px solid var(--border)',
    fontSize: '12px',
    fontWeight: 500,
    fontFamily: 'inherit',
    transition: 'all 0.15s',
  },
  badge: {
    fontSize: '9px',
    marginLeft: '4px',
    padding: '1px 4px',
    borderRadius: '4px',
    background: 'rgba(255,255,255,0.2)',
    fontWeight: 600,
  },
  textarea: {
    width: '100%',
    border: 'none',
    outline: 'none',
    color: 'var(--text-primary)',
    resize: 'vertical',
    lineHeight: 1.6,
    fontSize: '13px',
    fontFamily: 'inherit',
  },
  noteText: {
    fontSize: '13px',
    color: 'var(--text-primary)',
    lineHeight: 1.6,
    margin: 0,
    whiteSpace: 'pre-wrap',
  },
  muted: { fontSize: '12px', color: 'var(--text-secondary)' },
}
